import type { NivaasPackage, RateCardEntry } from '../types/package';
import { BuyerType } from '../types/package';

/**
 * Result of resolving a price from rate card or base pricing.
 */
export interface ResolvedPrice {
  /** Unit price for this configuration */
  unitPrice: {
    amount: number;
    currency: string;
  };
  /** GST percentage */
  gst: number;
  /** Whether the price is a total room price (not per-person) */
  isTotalPrice: boolean;
}

/**
 * Finds a rate card entry for the given offering, buyer type, and occupancy.
 */
export function findRateCardEntry(
  pkg: NivaasPackage | undefined,
  offeringId: string,
  buyerType: BuyerType,
  occupancy: number,
): RateCardEntry | undefined {
  if (!pkg?.rateCard) return undefined;

  return pkg.rateCard.find(
    (entry) =>
      entry.offering.id === offeringId &&
      entry.buyerType === buyerType &&
      entry.occupancy === occupancy,
  );
}

/**
 * Gets all available occupancy options for an offering from a package's rate card.
 * Returns sorted unique occupancy values.
 */
export function getOccupancyOptionsForOffering(
  pkg: NivaasPackage | undefined,
  offeringId: string,
  buyerType: BuyerType = BuyerType.PUBLIC,
): number[] {
  if (!pkg?.rateCard) return [];

  const occupancies = pkg.rateCard
    .filter((entry) => entry.offering.id === offeringId && entry.buyerType === buyerType)
    .map((entry) => entry.occupancy);

  // Return unique, sorted values
  return [...new Set(occupancies)].sort((a, b) => a - b);
}

/**
 * Gets all rate card entries for an offering (across all occupancies).
 */
export function getRateCardEntriesForOffering(
  pkg: NivaasPackage | undefined,
  offeringId: string,
  buyerType: BuyerType = BuyerType.PUBLIC,
): RateCardEntry[] {
  if (!pkg?.rateCard) return [];

  return pkg.rateCard.filter(
    (entry) => entry.offering.id === offeringId && entry.buyerType === buyerType,
  );
}

/**
 * Checks if a package has rate card pricing configured.
 */
export function hasRateCardPricing(pkg: NivaasPackage | undefined): boolean {
  return Boolean(pkg?.rateCard && pkg.rateCard.length > 0);
}

// Re-export BuyerType for convenience
export { BuyerType };
